import { DEFAULT_DRILL_RULES } from '@felix/domain';
import { ActionIcon, Alert, Button, Group, Stack, Tabs } from '@mantine/core';
import {
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconPlus,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import table from '../pms/Pms.module.css';
import work from '../pms/PmsWork.module.css';
import { LoadError, TablePager, TableSkeleton } from '../purchasing/table.js';
import {
  CancelPlanDialog,
  ConductDialog,
  DecisionDialog,
  type DecisionSubject,
  PlanDetailDrawer,
  PlanDrawer,
  PostponeDialog,
  PostponementTable,
  RuleDrawer,
  VariantDecisionDialog,
  VariantDrawer,
} from './DrillPlanDialogs.js';
import classes from './DrillPlanner.module.css';
import {
  DrillCell,
  DrillFilters,
  DrillStatusBadge,
  MarkDot,
  MarkLegend,
  ReviewBadge,
  shiftMonth,
  useDrillFilter,
  useMonthName,
  usePlannerData,
} from './DrillPlanParts.js';
import { intervalLabel } from './labels.js';
import type { DrillPlanSource } from './plan-source.js';
import type { DrillCompanyRule, DrillPlan, DrillPlanner, DrillVariant } from './plan-types.js';
import { annualRow, calendarMarks, filterPlans, monthItems, todayOf } from './plan-view.js';

export interface DrillPlannerScreenProps {
  source: DrillPlanSource;
}

const TABS = [
  'plans',
  'annual',
  'monthly',
  'calendar',
  'postponements',
  'variants',
  'rules',
] as const;
export type PlannerTab = (typeof TABS)[number];
type Tab = PlannerTab;

const PAGE_SIZE = 15;

/**
 * Operations ▸ Drills ▸ Planner (ADR 0128 Faz 2 Ek; ortağın belgesi §4): geminin tatbikat planı;
 * Drill, Frequency, Last Conducted, Next Due (kural motoru), Responsible Officer, Participants,
 * Postponement ve Status. Yıllık, aylık ve takvim görünümü; ertelemeler, gemiye özel senaryolar
 * ve şirket kural seti. Gemide plan, uygulama, erteleme ve kaptan kararı yazılır; ofiste filo
 * izlenir, varyant kararı ve kural seti yazılır. Süzgeç ve seçimler adreste kalır.
 */
export function DrillPlannerScreen({ source }: DrillPlannerScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillPlan.planner.${key}`, values);
  const filterState = useDrillFilter();
  const { query, setQuery, vesselId } = filterState;
  const { state, reload } = usePlannerData(source, source.scope === 'office' ? vesselId : null);
  const tab: Tab = TABS.find((x) => x === query.get('tab')) ?? 'plans';
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<{ plan: DrillPlan | null } | null>(null);
  const [conductFor, setConductFor] = useState<DrillPlan | null>(null);
  const [postponeFor, setPostponeFor] = useState<DrillPlan | null>(null);
  const [cancelFor, setCancelFor] = useState<DrillPlan | null>(null);
  const [variantFor, setVariantFor] = useState<DrillPlan | null>(null);
  const [decision, setDecision] = useState<DecisionSubject | null>(null);
  const [variantDecision, setVariantDecision] = useState<DrillVariant | null>(null);
  const [ruleEditing, setRuleEditing] = useState<{ rule: DrillCompanyRule | null } | null>(null);

  const data = state.kind === 'ready' ? state.data : null;
  const ship = data?.side === 'ship';
  const canCreate = ship && (data?.can.create ?? false) && Boolean(source.savePlan);

  const header = (
    <PageHeader
      title={t('menu.operationsDrillsPlanner')}
      description={k(source.scope === 'office' ? 'officeDescription' : 'description')}
      actions={
        canCreate ? (
          <Button
            leftSection={<IconPlus size={16} aria-hidden />}
            onClick={() => setEditing({ plan: null })}
          >
            {t('drillPlan.actions.newPlan')}
          </Button>
        ) : undefined
      }
    />
  );

  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={8} label={t('menu.operationsDrillsPlanner')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError code={state.code} title={k('error')} onRetry={() => void reload()} />
        </Panel>
      </div>
    );
  }

  const ready = state.data;
  const today = todayOf(ready.vessels);
  const selected = ready.plans.find((p) => p.id === query.get('plan')) ?? null;
  const refreshed = async (plan?: DrillPlan) => {
    await reload();
    if (plan) setQuery({ plan: plan.id });
  };

  return (
    <div className={table.screen}>
      {header}
      <RedrillNotice data={ready} onOpen={(planId) => setQuery({ plan: planId })} />
      <Panel>
        <DrillFilters data={ready} state={filterState} />
        <Tabs
          value={tab}
          onChange={(value) => {
            setPage(1);
            setQuery({ tab: value === 'plans' ? null : value });
          }}
          className={work.pageTabs}
        >
          <Tabs.List>
            {TABS.map((x) => (
              <Tabs.Tab key={x} value={x}>
                {k(`tabs.${x}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <PlannerTabBody
          tab={tab}
          data={ready}
          today={today}
          filterState={filterState}
          page={page}
          onPage={setPage}
          onOpen={(plan) => setQuery({ plan: plan.id })}
          onDecideVariant={setVariantDecision}
          onEditVariant={(variant) => {
            const plan = ready.plans.find(
              (p) => p.templateId === variant.templateId && p.vesselId === variant.vesselId,
            );
            if (plan) setVariantFor(plan);
          }}
          onEditRule={(rule) => setRuleEditing({ rule })}
          canEditRules={ready.can.editRules && Boolean(source.saveRule)}
        />
      </Panel>
      <PlanDetailDrawer
        plan={selected}
        data={ready}
        source={source}
        onClose={() => setQuery({ plan: null })}
        onEdit={() => selected && setEditing({ plan: selected })}
        onConduct={() => setConductFor(selected)}
        onPostpone={() => setPostponeFor(selected)}
        onCancelPlan={() => setCancelFor(selected)}
        onDecide={setDecision}
        onVariant={() => setVariantFor(selected)}
      />
      <PlanDrawer
        opened={editing !== null}
        data={ready}
        plan={editing?.plan ?? null}
        source={source}
        onClose={() => setEditing(null)}
        onSaved={(plan) => {
          setEditing(null);
          void refreshed(plan);
        }}
      />
      <ConductDialog
        plan={conductFor}
        today={today}
        source={source}
        onClose={() => setConductFor(null)}
        onSaved={(plan) => {
          setConductFor(null);
          void refreshed(plan);
        }}
      />
      <PostponeDialog
        plan={postponeFor}
        today={today}
        source={source}
        onClose={() => setPostponeFor(null)}
        onSaved={(plan) => {
          setPostponeFor(null);
          void refreshed(plan);
        }}
      />
      <CancelPlanDialog
        plan={cancelFor}
        source={source}
        onClose={() => setCancelFor(null)}
        onSaved={(plan) => {
          setCancelFor(null);
          void refreshed(plan);
        }}
      />
      <DecisionDialog
        plan={selected}
        subject={decision}
        data={ready}
        source={source}
        onClose={() => setDecision(null)}
        onSaved={(plan) => {
          setDecision(null);
          void refreshed(plan);
        }}
      />
      <VariantDrawer
        plan={variantFor}
        data={ready}
        source={source}
        onClose={() => setVariantFor(null)}
        onSaved={() => {
          setVariantFor(null);
          void reload();
        }}
      />
      <VariantDecisionDialog
        variant={variantDecision}
        source={source}
        onClose={() => setVariantDecision(null)}
        onSaved={() => {
          setVariantDecision(null);
          void reload();
        }}
      />
      <RuleDrawer
        opened={ruleEditing !== null}
        rule={ruleEditing?.rule ?? null}
        source={source}
        onClose={() => setRuleEditing(null)}
        onSaved={() => {
          setRuleEditing(null);
          void reload();
        }}
      />
    </div>
  );
}

export function PlannerTabBody({
  tab,
  data,
  today,
  filterState,
  page,
  onPage,
  onOpen,
  onDecideVariant,
  onEditVariant,
  onEditRule,
  canEditRules,
}: {
  tab: Tab;
  data: DrillPlanner;
  today: string;
  filterState: ReturnType<typeof useDrillFilter>;
  page: number;
  onPage: (page: number) => void;
  onOpen: (plan: DrillPlan) => void;
  onDecideVariant: (variant: DrillVariant) => void;
  onEditVariant: (variant: DrillVariant) => void;
  onEditRule: (rule: DrillCompanyRule | null) => void;
  canEditRules: boolean;
}) {
  const plans = useMemo(
    () => filterPlans(data.plans, filterState.filter),
    [data.plans, filterState.filter],
  );
  const fleet = data.side === 'office' && filterState.vesselId === null;
  switch (tab) {
    case 'plans':
      return (
        <PlansTable
          plans={plans}
          total={data.plans.length}
          fleet={fleet}
          page={page}
          onPage={onPage}
          onOpen={onOpen}
        />
      );
    case 'annual':
      return <AnnualView plans={plans} today={today} fleet={fleet} onOpen={onOpen} />;
    case 'monthly':
      return <MonthlyView plans={plans} today={today} fleet={fleet} onOpen={onOpen} />;
    case 'calendar':
      return <CalendarView plans={plans} today={today} fleet={fleet} onOpen={onOpen} />;
    case 'postponements': {
      const ids = new Set(plans.map((p) => p.id));
      const items = data.plans
        .filter((p) => ids.has(p.id))
        .flatMap((p) => p.postponements)
        .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));
      return items.length === 0 ? (
        <EmptyStateText keyName="noPostponements" />
      ) : (
        <PostponementTable items={items} fleet />
      );
    }
    case 'variants':
      return (
        <VariantsTable
          data={data}
          onDecide={onDecideVariant}
          onEdit={onEditVariant}
          fleet={data.side === 'office'}
        />
      );
    case 'rules':
      return <RulesView data={data} onEdit={onEditRule} canEdit={canEditRules} />;
  }
}

function EmptyStateText({ keyName }: { keyName: string }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<IconCalendarEvent size={22} />}
      title={t(`drillPlan.planner.empty.${keyName}`)}
    />
  );
}

/** Belge §4 Planner alanları: tatbikat, sıklık, son uygulama, vade, sorumlu, katılımcı, erteleme, durum. */
function PlansTable({
  plans,
  total,
  fleet,
  page,
  onPage,
  onOpen,
}: {
  plans: DrillPlan[];
  total: number;
  fleet: boolean;
  page: number;
  onPage: (page: number) => void;
  onOpen: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`drillPlan.columns.${key}`);
  if (total === 0) return <EmptyStateText keyName="noPlans" />;
  if (plans.length === 0) return <EmptyStateText keyName="noMatch" />;
  const sorted = [...plans].sort(
    (a, b) =>
      a.due.nextDueOn.localeCompare(b.due.nextDueOn) ||
      a.templateCode.localeCompare(b.templateCode),
  );
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const rows = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return (
    <>
      <section
        className={table.tableBox}
        aria-label={t('drillPlan.planner.tabs.plans')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={table.table} data-cards>
          <thead>
            <tr>
              {fleet ? <th scope="col">{c('vessel')}</th> : null}
              <th scope="col">{c('drill')}</th>
              <th scope="col">{c('frequency')}</th>
              <th scope="col">{c('lastConducted')}</th>
              <th scope="col">{c('nextDue')}</th>
              <th scope="col">{c('responsible')}</th>
              <th scope="col">{c('participants')}</th>
              <th scope="col">{c('postponement')}</th>
              <th scope="col">{c('status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((plan) => {
              const pending = plan.postponements.find((p) => p.status === 'pending');
              const inEffect = plan.postponements.find((p) => p.effect === 'in_effect');
              return (
                <tr key={plan.id}>
                  {fleet ? <td data-label={c('vessel')}>{plan.vesselName ?? '—'}</td> : null}
                  <td data-label={c('drill')} className={classes.wrapCell}>
                    <DrillCell plan={plan} onOpen={() => onOpen(plan)} />
                  </td>
                  <td data-label={c('frequency')}>{intervalLabel(t, plan.intervalMonths)}</td>
                  <td data-label={c('lastConducted')}>{formatDay(plan.lastConductedOn)}</td>
                  <td data-label={c('nextDue')}>
                    {plan.state === 'active' ? formatDay(plan.due.nextDueOn) : '—'}
                    {plan.due.basis === 'crew_change' ? (
                      <>
                        <br />
                        <span className={classes.muted}>{t('drillPlan.basis.crew_change')}</span>
                      </>
                    ) : null}
                  </td>
                  <td data-label={c('responsible')}>{plan.responsibleName ?? '—'}</td>
                  <td data-label={c('participants')} className={classes.wrapCell}>
                    {plan.participantRoles.join(', ') || '—'}
                  </td>
                  <td data-label={c('postponement')}>
                    {pending ? (
                      <ReviewBadge value="pending" />
                    ) : inEffect ? (
                      <span>
                        {formatDay(inEffect.newDueOn)}
                        <br />
                        <span className={classes.muted}>
                          {t('drillPlan.originalShort', {
                            date: formatDay(inEffect.firstOriginalDueOn),
                          })}
                        </span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td data-label={c('status')}>
                    <DrillStatusBadge status={plan.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      {sorted.length > PAGE_SIZE ? (
        <TablePager
          page={current}
          pages={pages}
          summary={t('drills.showing', {
            from: (current - 1) * PAGE_SIZE + 1,
            to: Math.min(current * PAGE_SIZE, sorted.length),
            total: sorted.length,
          })}
          label={t('drillPlan.planner.tabs.plans')}
          onPage={onPage}
        />
      ) : null}
    </>
  );
}

function Stepper({
  label,
  onPrev,
  onNext,
}: {
  label: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  return (
    <span className={classes.stepper}>
      <ActionIcon
        variant="subtle"
        size="sm"
        aria-label={t('drillPlan.planner.previous')}
        onClick={onPrev}
      >
        <IconChevronLeft size={15} />
      </ActionIcon>
      <span aria-live="polite">{label}</span>
      <ActionIcon
        variant="subtle"
        size="sm"
        aria-label={t('drillPlan.planner.next')}
        onClick={onNext}
      >
        <IconChevronRight size={15} />
      </ActionIcon>
    </span>
  );
}

/** Yıllık görünüm: her plan bir satır, 12 ay; yapılan, vade, ertelenen ve geciken işaretleri. */
function AnnualView({
  plans,
  today,
  fleet,
  onOpen,
}: {
  plans: DrillPlan[];
  today: string;
  fleet: boolean;
  onOpen: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const monthName = useMonthName();
  const [year, setYear] = useState(() => Number(today.slice(0, 4)));
  if (plans.length === 0) return <EmptyStateText keyName="noMatch" />;
  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  return (
    <>
      <div className={classes.bar}>
        <Stepper
          label={String(year)}
          onPrev={() => setYear(year - 1)}
          onNext={() => setYear(year + 1)}
        />
      </div>
      <section
        className={table.tableBox}
        aria-label={t('drillPlan.planner.tabs.annual')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={`${table.table} ${classes.annual}`}>
          <thead>
            <tr>
              <th scope="col">{t('drillPlan.columns.drill')}</th>
              {months.map((m) => (
                <th key={m} scope="col" data-month>
                  {monthName(m, 'short')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => {
              const row = annualRow(plan, year, today);
              return (
                <tr key={plan.id}>
                  <td>
                    <DrillCell plan={plan} onOpen={() => onOpen(plan)} />
                    {fleet ? <span className={classes.muted}>{plan.vesselName}</span> : null}
                  </td>
                  {row.map((mark, i) => (
                    <td key={months[i]} data-month>
                      {mark ? <MarkDot mark={mark} /> : null}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <MarkLegend />
    </>
  );
}

function MonthList({
  plans,
  month,
  today,
  fleet,
  onOpen,
  day,
}: {
  plans: DrillPlan[];
  month: string;
  today: string;
  fleet: boolean;
  onOpen: (plan: DrillPlan) => void;
  day?: string | null;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`drillPlan.columns.${key}`);
  const items = monthItems(plans, month, today).filter((i) => !day || i.date === day);
  if (items.length === 0) return <EmptyStateText keyName="noEvents" />;
  return (
    <div className={table.tableBox}>
      <table className={table.table} data-cards>
        <thead>
          <tr>
            <th scope="col">{c('date')}</th>
            {fleet ? <th scope="col">{c('vessel')}</th> : null}
            <th scope="col">{c('drill')}</th>
            <th scope="col">{c('event')}</th>
            <th scope="col">{c('responsible')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={`${item.plan.id}-${item.date}-${item.mark}`}>
              <td data-label={c('date')}>{formatDay(item.date)}</td>
              {fleet ? <td data-label={c('vessel')}>{item.plan.vesselName ?? '—'}</td> : null}
              <td data-label={c('drill')}>
                <DrillCell plan={item.plan} onOpen={() => onOpen(item.plan)} />
              </td>
              <td data-label={c('event')}>
                <span className={classes.actions}>
                  <MarkDot mark={item.mark} />
                  {t(`drillPlan.marks.${item.mark}`)}
                </span>
              </td>
              <td data-label={c('responsible')}>{item.plan.responsibleName ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Aylık görünüm: ayın vadeleri ve uygulamaları. */
function MonthlyView(props: {
  plans: DrillPlan[];
  today: string;
  fleet: boolean;
  onOpen: (plan: DrillPlan) => void;
}) {
  const monthName = useMonthName();
  const [month, setMonth] = useState(() => props.today.slice(0, 7));
  return (
    <>
      <div className={classes.bar}>
        <Stepper
          label={monthName(month)}
          onPrev={() => setMonth(shiftMonth(month, -1))}
          onNext={() => setMonth(shiftMonth(month, 1))}
        />
      </div>
      <MonthList {...props} month={month} />
    </>
  );
}

/** Takvim: ayın ızgarası, günün işaretleri; gün seçilince o günün listesi. Telefonda yalnız liste. */
function CalendarView(props: {
  plans: DrillPlan[];
  today: string;
  fleet: boolean;
  onOpen: (plan: DrillPlan) => void;
}) {
  const { t, i18n } = useTranslation();
  const monthName = useMonthName();
  const [month, setMonth] = useState(() => props.today.slice(0, 7));
  const [day, setDay] = useState<string | null>(null);
  const marks = useMemo(
    () => calendarMarks(props.plans, month, props.today),
    [props.plans, month, props.today],
  );
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-GB';
  const weekdays = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    return Array.from({ length: 7 }, (_, i) =>
      format.format(new Date(Date.UTC(2024, 0, 1 + i))).replace('.', ''),
    );
  }, [locale]);
  const [y, m] = month.split('-').map(Number);
  const first = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, 1));
  const days = new Date(Date.UTC(y ?? 2000, m ?? 1, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, n) => `${month}-${String(n + 1).padStart(2, '0')}`),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  return (
    <>
      <div className={classes.bar}>
        <Stepper
          label={monthName(month)}
          onPrev={() => {
            setMonth(shiftMonth(month, -1));
            setDay(null);
          }}
          onNext={() => {
            setMonth(shiftMonth(month, 1));
            setDay(null);
          }}
        />
        {day ? (
          <Button size="xs" variant="subtle" onClick={() => setDay(null)}>
            {t('drillPlan.planner.wholeMonth')}
          </Button>
        ) : null}
      </div>
      <div className={classes.calendarBox}>
        <section className={classes.calendar} aria-label={monthName(month)}>
          {weekdays.map((w) => (
            <div key={w} className={classes.weekday} aria-hidden>
              {w}
            </div>
          ))}
          {cells.map((cell, index) =>
            cell ? (
              <button
                key={cell}
                type="button"
                className={classes.day}
                data-today={cell === props.today ? '' : undefined}
                aria-pressed={day === cell}
                aria-label={`${formatDay(cell)}${
                  marks.get(cell)
                    ? ` · ${(marks.get(cell) ?? []).map((x) => t(`drillPlan.marks.${x}`)).join(', ')}`
                    : ''
                }`}
                onClick={() => setDay(day === cell ? null : cell)}
              >
                <span>{Number(cell.slice(8))}</span>
                <span className={classes.dots}>
                  {(marks.get(cell) ?? []).map((x) => (
                    <span key={x} className={classes.mark} data-mark={x} aria-hidden />
                  ))}
                </span>
              </button>
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: ayın boş hücreleri konuma bağlı
              <span key={`empty-${index}`} className={classes.day} data-empty aria-hidden />
            ),
          )}
        </section>
        <MarkLegend />
      </div>
      <div className={classes.section}>
        <MonthList {...props} month={month} day={day} />
      </div>
    </>
  );
}

/** Gemiye özel senaryolar: durum, temel sürüm, gerekçe; gemide düzenleme, ofiste karar. */
function VariantsTable({
  data,
  fleet,
  onDecide,
  onEdit,
}: {
  data: DrillPlanner;
  fleet: boolean;
  onDecide: (variant: DrillVariant) => void;
  onEdit: (variant: DrillVariant) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`drillPlan.variant.${key}`);
  if (data.variants.length === 0) return <EmptyStateText keyName="noVariants" />;
  const action = data.side === 'office' ? data.can.decideVariant : data.can.edit;
  return (
    <div className={table.tableBox}>
      <table className={table.table} data-cards>
        <thead>
          <tr>
            {fleet ? <th scope="col">{c('vessel')}</th> : null}
            <th scope="col">{t('drillPlan.columns.drill')}</th>
            <th scope="col">{c('baseVersion')}</th>
            <th scope="col">{c('reason')}</th>
            <th scope="col">{c('updated')}</th>
            <th scope="col">{c('status')}</th>
            {action ? (
              <th scope="col">
                <span className={table.srOnly}>{c('actions')}</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {data.variants.map((v) => (
            <tr key={v.id}>
              {fleet ? <td data-label={c('vessel')}>{v.vesselName ?? '—'}</td> : null}
              <td data-label={t('drillPlan.columns.drill')}>
                {v.templateCode} · {v.templateName}
              </td>
              <td data-label={c('baseVersion')}>
                v{v.baseVersion}
                {v.templateVersion && v.templateVersion > v.baseVersion ? (
                  <>
                    <br />
                    <span className={classes.muted}>
                      {t('drillPlan.variant.newerTemplate', { version: v.templateVersion })}
                    </span>
                  </>
                ) : null}
              </td>
              <td data-label={c('reason')}>{v.reason}</td>
              <td data-label={c('updated')}>
                {v.createdByName ?? '—'}
                <br />
                <span className={classes.muted}>{formatDateTime(v.updatedAt)}</span>
              </td>
              <td data-label={c('status')}>
                <ReviewBadge value={v.status} />
              </td>
              {action ? (
                <td data-label={c('actions')}>
                  {data.side === 'office' ? (
                    <Button size="xs" variant="default" onClick={() => onDecide(v)}>
                      {v.status === 'pending' ? c('review') : c('view')}
                    </Button>
                  ) : (
                    <ActionIcon variant="subtle" aria-label={c('edit')} onClick={() => onEdit(v)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Kural seti: ürünün varsayılanı ve şirketin koşullu satırları (gerekçe, son düzelten). */
function RulesView({
  data,
  canEdit,
  onEdit,
}: {
  data: DrillPlanner;
  canEdit: boolean;
  onEdit: (rule: DrillCompanyRule | null) => void;
}) {
  const { t } = useTranslation();
  const r = (key: string) => t(`drillPlan.rules.${key}`);
  const value = (months: number | null, hours: number | null) =>
    months !== null
      ? t('drillPlan.months', { count: months })
      : hours !== null
        ? t('drillPlan.hours', { count: hours })
        : '—';
  return (
    <>
      <div className={classes.bar}>
        <p className={classes.muted}>{r('note')}</p>
        {canEdit ? (
          <Button
            size="xs"
            leftSection={<IconPlus size={14} aria-hidden />}
            onClick={() => onEdit(null)}
          >
            {r('add')}
          </Button>
        ) : null}
      </div>
      <h3 className={classes.sectionTitle}>{r('company')}</h3>
      {data.ruleSet.rules.length === 0 ? (
        <p className={classes.muted}>{r('noCompanyRules')}</p>
      ) : (
        <div className={table.tableBox}>
          <table className={table.table} data-cards>
            <thead>
              <tr>
                <th scope="col">{r('code')}</th>
                <th scope="col">{r('condition')}</th>
                <th scope="col">{r('value')}</th>
                <th scope="col">{r('reason')}</th>
                <th scope="col">{r('updatedBy')}</th>
                <th scope="col">{r('status')}</th>
                {canEdit ? (
                  <th scope="col">
                    <span className={table.srOnly}>{r('edit')}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {data.ruleSet.rules.map((rule) => (
                <tr key={rule.id}>
                  <td data-label={r('code')}>{rule.code}</td>
                  <td data-label={r('condition')}>
                    {[...rule.vesselTypes, ...rule.flags].join(', ') || r('allVessels')}
                  </td>
                  <td data-label={r('value')}>
                    {rule.applicable
                      ? value(rule.intervalMonths, rule.triggerHours)
                      : r('notApplicable')}
                  </td>
                  <td data-label={r('reason')}>{rule.reason}</td>
                  <td data-label={r('updatedBy')}>
                    {rule.updatedByName ?? '—'}
                    <br />
                    <span className={classes.muted}>{formatDateTime(rule.updatedAt)}</span>
                  </td>
                  <td data-label={r('status')}>
                    <ReviewBadge value={rule.active ? 'in_effect' : 'ended'} />
                  </td>
                  {canEdit ? (
                    <td data-label={r('edit')}>
                      <ActionIcon
                        variant="subtle"
                        aria-label={r('edit')}
                        onClick={() => onEdit(rule)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3 className={`${classes.sectionTitle} ${classes.section}`}>{r('defaults')}</h3>
      <div className={table.tableBox}>
        <table className={table.table} data-cards>
          <thead>
            <tr>
              <th scope="col">{r('code')}</th>
              <th scope="col">{r('title')}</th>
              <th scope="col">{r('reference')}</th>
              <th scope="col">{r('value')}</th>
              <th scope="col">{r('basisLabel')}</th>
            </tr>
          </thead>
          <tbody>
            {DEFAULT_DRILL_RULES.map((rule) => (
              <tr key={rule.code}>
                <td data-label={r('code')}>{rule.code}</td>
                <td data-label={r('title')}>{rule.title}</td>
                <td data-label={r('reference')}>{rule.reference}</td>
                <td data-label={r('value')}>
                  {value(rule.intervalMonths, rule.triggerHours)}
                  {rule.scope === 'passenger' ? ` · ${r('passengerOnly')}` : ''}
                </td>
                <td data-label={r('basisLabel')}>{r(`basis.${rule.basis}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * Tekrar tatbikat önerileri (ADR 0128 Faz 4 Ek): bulgu tekrar tatbikat istedi, bağlı kaydı
 * kapandı ve o zamandan beri onaylı kayıt yok. Öneri yazılmaz; plan kişinin eylemiyle değişir.
 */
function RedrillNotice({ data, onOpen }: { data: DrillPlanner; onOpen: (planId: string) => void }) {
  const { t } = useTranslation();
  const redrills = data.redrills ?? [];
  if (redrills.length === 0) return null;
  return (
    <Alert color="yellow" title={t('drillFinding.redrill.title')} data-testid="redrills">
      <Stack gap={6}>
        <span>{t('drillFinding.redrill.hint')}</span>
        {redrills.map((r) => (
          <Group key={r.findingId} justify="space-between" gap="xs" wrap="wrap">
            <span>
              <strong>
                {r.templateCode ? `${r.templateCode} · ` : ''}
                {r.templateName ?? '—'}
              </strong>
              {' — '}
              {t('drillFinding.redrill.from', { finding: r.findingNo, link: r.closedLink })}
            </span>
            {r.planId ? (
              <Button size="xs" variant="light" onClick={() => onOpen(r.planId as string)}>
                {t('drillFinding.redrill.open')}
              </Button>
            ) : null}
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}
